import * as React from "react";
import { AnalyticsSpinner } from "../shared";

/**
 * BillingEventsList Component
 * 
 * Displays detailed billing events for a specific date with subscription information
 */

interface BillingEvent {
  date: string;
  subscription_id: string;
  customer_email: string;
  plan_name: string;
  expected_amount: number;
  actual_amount: number;
  status: 'paid' | 'pending';
  invoice_id?: string;
  billing_reason: string;
}

interface DaySummary {
  expected_amount: number;
  actual_amount: number;
  status: string;
  billing_events_count: number;
}

interface BillingEventsData {
  date: string;
  date_display: string;
  day_summary: DaySummary;
  events: BillingEvent[];
}

interface BillingEventsListProps {
  selectedDate?: string;
  isLoading?: boolean;
  error?: string | null;
  className?: string;
  onClose?: () => void;
  onSubscriptionClick?: (subscriptionId: string) => void;
  onInvoiceClick?: (invoiceId: string) => void;
  filters?: {
    plan_filter?: string;
    utm_source_filter?: string;
  };
}

const BillingEventsList: React.FC<BillingEventsListProps> = ({
  selectedDate,
  isLoading = false,
  error = null,
  className = "",
  onClose,
  onSubscriptionClick,
  onInvoiceClick,
  filters = {}
}) => {
  const [eventsData, setEventsData] = React.useState<BillingEventsData | null>(null);
  const [isLoadingData, setIsLoadingData] = React.useState(false);

  // Fetch billing events for the selected date
  const fetchBillingEvents = React.useCallback(async (date: string) => {
    setIsLoadingData(true);
    try {
      const params = new URLSearchParams();
      params.append('date', date);
      
      if (filters.plan_filter) params.append('plan_filter', filters.plan_filter);
      if (filters.utm_source_filter) params.append('utm_source_filter', filters.utm_source_filter);

      const url = `/admin/analytics/subscriptions/billing_events_for_date.json?${params}`;

      const response = await fetch(url, {
        credentials: 'same-origin',
        headers: {
          'Accept': 'application/json',
          'X-CSRF-Token': document.querySelector('[name="csrf-token"]')?.getAttribute('content') || ''
        }
      });
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const data = await response.json();
      setEventsData(data);
    } catch {
      setEventsData(null);
    } finally {
      setIsLoadingData(false);
    }
  }, [filters.plan_filter, filters.utm_source_filter]);

  // Fetch data when selected date changes
  React.useEffect(() => {
    if (selectedDate) {
      fetchBillingEvents(selectedDate);
    } else {
      setEventsData(null);
    }
  }, [selectedDate, fetchBillingEvents]);

  // Format currency
  const formatCurrency = (amount: number): string => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(amount);
  };

  // Get status badge variant
  const getStatusVariant = (status: string): string => {
    switch (status) {
      case 'paid':
        return 'success';
      case 'pending':
        return 'warning';
      default:
        return 'secondary';
    }
  };

  // Get billing reason display
  const getBillingReasonDisplay = (reason: string): string => {
    switch (reason) {
      case 'subscription_cycle':
        return 'Regular Billing';
      case 'subscription_create':
        return 'Initial Billing';
      case 'subscription_update':
        return 'Plan Update';
      case 'manual':
        return 'Manual Billing';
      default:
        return reason.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
    }
  };

  // Handle subscription click
  const handleSubscriptionClick = (event: React.MouseEvent, subscriptionId: string) => {
    event.preventDefault();
    if (onSubscriptionClick) {
      onSubscriptionClick(subscriptionId);
    }
  };

  // Handle invoice click
  const handleInvoiceClick = (event: React.MouseEvent, invoiceId: string) => {
    event.preventDefault();
    if (onInvoiceClick) {
      onInvoiceClick(invoiceId);
    }
  };

  if (!selectedDate) {
    return (
      <div className={`billing-events-list ${className}`.trim()}>
        <div className="card">
          <div className="card-body text-center py-5 text-muted">
            <i className="fas fa-calendar-day fa-2x mb-3" aria-hidden="true"></i>
            <p className="mb-0">Select a date on the calendar to view billing events</p>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className={`billing-events-list ${className}`.trim()}>
        <div className="card">
          <div className="card-header">
            <div className="d-flex justify-content-between align-items-center">
              <h6 className="mb-0">
                <i className="fas fa-calendar-day me-2" aria-hidden="true"></i>
                Billing Events
              </h6>
              {onClose && (
                <button
                  type="button"
                  className="btn-close"
                  aria-label="Close"
                  onClick={onClose}
                ></button>
              )}
            </div>
          </div>
          <div className="card-body">
            <div className="alert alert-danger">
              <i className="fas fa-exclamation-triangle me-2" aria-hidden="true"></i>
              <strong>Error:</strong> {error}
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (isLoading || isLoadingData) {
    return (
      <div className={`billing-events-list ${className}`.trim()}>
        <div className="card">
          <div className="card-header">
            <div className="d-flex justify-content-between align-items-center">
              <h6 className="mb-0">
                <i className="fas fa-calendar-day me-2" aria-hidden="true"></i>
                Billing Events
              </h6>
              {onClose && (
                <button
                  type="button"
                  className="btn-close"
                  aria-label="Close"
                  onClick={onClose}
                ></button>
              )}
            </div>
          </div>
          <div className="card-body">
            <AnalyticsSpinner
              height={300}
              message="Loading billing events..."
            />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={`billing-events-list ${className}`.trim()}>
      <div className="card">
        <div className="card-header">
          <div className="d-flex justify-content-between align-items-center">
            <div>
              <h6 className="mb-1">
                <i className="fas fa-calendar-day me-2" aria-hidden="true"></i>
                Billing Events
              </h6>
              {eventsData && (
                <small className="text-muted">{eventsData.date_display}</small>
              )}
            </div>
            {onClose && (
              <button
                type="button"
                className="btn-close"
                aria-label="Close"
                onClick={onClose}
              ></button>
            )}
          </div>
        </div>

        <div className="card-body">
          {eventsData ? (
            <>
              {/* Day Summary */}
              <div className="row mb-4">
                <div className="col-md-3">
                  <div className="text-center p-3 bg-light rounded">
                    <div className="h5 mb-1 text-primary">
                      {formatCurrency(eventsData.day_summary.expected_amount)}
                    </div>
                    <div className="small text-muted">Expected</div>
                  </div>
                </div>
                <div className="col-md-3">
                  <div className="text-center p-3 bg-light rounded">
                    <div className="h5 mb-1 text-success">
                      {formatCurrency(eventsData.day_summary.actual_amount)}
                    </div>
                    <div className="small text-muted">Billed</div>
                  </div>
                </div>
                <div className="col-md-3">
                  <div className="text-center p-3 bg-light rounded">
                    <div className="h5 mb-1 text-info">
                      {eventsData.day_summary.billing_events_count}
                    </div>
                    <div className="small text-muted">Events</div>
                  </div>
                </div>
                <div className="col-md-3">
                  <div className="text-center p-3 bg-light rounded">
                    <div className="h5 mb-1">
                      <span className={`badge bg-${getStatusVariant(eventsData.day_summary.status)}`}>
                        {eventsData.day_summary.status.replace(/_/g, ' ').toUpperCase()}
                      </span>
                    </div>
                    <div className="small text-muted">Status</div>
                  </div>
                </div>
              </div>

              {/* Events List */}
              {eventsData.events.length > 0 ? (
                <div className="table-responsive">
                  <table className="table table-hover">
                    <thead className="table-light">
                      <tr>
                        <th scope="col">Customer</th>
                        <th scope="col">Plan</th>
                        <th scope="col">Expected</th>
                        <th scope="col">Actual</th>
                        <th scope="col">Status</th>
                        <th scope="col">Reason</th>
                        <th scope="col">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {eventsData.events.map((event, index) => (
                        <tr key={`${event.subscription_id}-${index}`}>
                          <td>
                            <div className="d-flex flex-column">
                              <span className="fw-medium">{event.customer_email}</span>
                              <small className="text-muted">{event.subscription_id}</small>
                            </div>
                          </td>
                          <td>
                            <span className="badge bg-secondary">{event.plan_name}</span>
                          </td>
                          <td>
                            <span className="text-primary fw-medium">
                              {formatCurrency(event.expected_amount)}
                            </span>
                          </td>
                          <td>
                            {event.actual_amount > 0 ? (
                              <span className="text-success fw-medium">
                                {formatCurrency(event.actual_amount)}
                              </span>
                            ) : (
                              <span className="text-muted">—</span>
                            )}
                          </td>
                          <td>
                            <span className={`badge bg-${getStatusVariant(event.status)}`}>
                              {event.status.toUpperCase()}
                            </span>
                          </td>
                          <td>
                            <small className="text-muted">
                              {getBillingReasonDisplay(event.billing_reason)}
                            </small>
                          </td>
                          <td>
                            <div className="btn-group-sm">
                              <button
                                type="button"
                                className="btn btn-outline-primary btn-sm me-1"
                                onClick={(e) => handleSubscriptionClick(e, event.subscription_id)}
                                title="View Subscription"
                              >
                                <i className="fas fa-eye" aria-hidden="true"></i>
                              </button>
                              {event.invoice_id && (
                                <button
                                  type="button"
                                  className="btn btn-outline-success btn-sm"
                                  onClick={(e) => handleInvoiceClick(e, event.invoice_id!)}
                                  title="View Invoice"
                                >
                                  <i className="fas fa-file-invoice" aria-hidden="true"></i>
                                </button>
                              )}
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="text-center py-4 text-muted">
                  <i className="fas fa-inbox fa-2x mb-3" aria-hidden="true"></i>
                  <p className="mb-0">No billing events for this date</p>
                </div>
              )}
            </>
          ) : (
            <div className="text-center py-5 text-muted">
              <i className="fas fa-calendar-day fa-2x mb-3" aria-hidden="true"></i>
              <p className="mb-0">No data available for the selected date</p>
            </div>
          )}
        </div>
      </div>

      <style jsx>{`
        .billing-events-list .table th {
          border-top: none;
          font-weight: 600;
          font-size: 0.875rem;
        }

        .billing-events-list .table td {
          vertical-align: middle;
        }

        .billing-events-list .btn-group-sm .btn {
          padding: 0.25rem 0.5rem;
          font-size: 0.75rem;
        }

        .billing-events-list .bg-light {
          background-color: rgba(var(--bs-gray-100-rgb), 0.5) !important;
        }

        @media (max-width: 768px) {
          .billing-events-list .row .col-md-3 {
            margin-bottom: 1rem;
          }
          
          .billing-events-list .table-responsive {
            font-size: 0.875rem;
          }
          
          .billing-events-list .btn-group-sm {
            display: flex;
            flex-direction: column;
            gap: 0.25rem;
          }
          
          .billing-events-list .btn-group-sm .btn {
            width: 100%;
          }
        }
      `}</style>
    </div>
  );
};

BillingEventsList.displayName = 'BillingEventsList';

export default BillingEventsList;
export type { BillingEvent, DaySummary, BillingEventsData };
